import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useBridgewayApi } from '@/api/BridgewayApi';
import { RestApiError } from '@/api/RestApi';
import { useUserId } from '@/auth/UserIdContext';
import { logger } from '@/logger';

import styles from './ActivateStrategy.module.css';

/**
 * The page an SMS link opens so the owner can turn a strategy on.
 *
 * Turning a strategy on is the owner's commitment to the whole plan. It is what
 * starts the daily review, and it builds every campaign in the plan and raises
 * one setup notification per campaign. The SMS thread used to skip it entirely
 * and offer a single campaign to start, which asked the owner to run work they
 * had never agreed to.
 *
 * Nothing here sends a message to a customer. Activation builds and schedules;
 * each campaign still needs its own explicit start.
 */

type Campaign = { id: string; title?: string; aim?: string; status?: string };

type Strategy = {
	status?: string;
	direction?: { statement?: string; wish?: { outcome?: string } };
	goal?: { outcome?: string; target?: number | string };
	campaign_proposals?: Campaign[];
};

/**
 * The daily review lives on the AGENT document, not on the run.
 *
 * Pausing writes nothing to the strategy: `pause_strategy_agent` moves the agent
 * to `paused` and the run keeps `status: "active"`. So the run's status alone
 * cannot tell a running strategy from a paused one, and reading it alone told an
 * owner who had just paused that Bridgeway still reviewed their plan daily.
 *
 * The agent is named `strategy:<id>`, which is how the server finds it too.
 */
const STRATEGY_AGENT_PREFIX = 'strategy:';

type AgentRow = { name?: string; status?: string };

const reviewStatusOf = (
	agents: AgentRow[],
	strategyId: string,
): string | undefined =>
	agents.find((a) => a.name === `${STRATEGY_AGENT_PREFIX}${strategyId}`)
		?.status;

export const ActivateStrategy = () => {
	const { strategyId = '' } = useParams();
	const api = useBridgewayApi();
	const userId = useUserId();

	const [strategy, setStrategy] = useState<Strategy | null>(null);
	const [loadError, setLoadError] = useState<'missing' | 'unavailable' | null>(
		null,
	);
	const [reloadKey, setReloadKey] = useState(0);
	const [busy, setBusy] = useState<'activate' | 'reject' | 'resume' | null>(
		null,
	);
	const [done, setDone] = useState<'activated' | 'rejected' | 'paused' | null>(
		null,
	);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		const load = async () => {
			try {
				const doc = await api.get<Strategy>(
					`/api/user/${userId}/strategies/${strategyId}`,
				);
				if (cancelled) return;
				setStrategy(doc);
				// Already running before this page loaded. Show it as on rather
				// than offering a button that would be refused. A paused run also
				// reads `active`, so it needs its own answer: the review is off,
				// the campaigns stopped, and only Resume brings them back.
				if (doc.status !== 'active') return;

				// A second request, because the pause state is not on the run. It
				// must not break the page: a strategy that reads `active` and an
				// agent we cannot see is the state this page has always shown as
				// on, so a failure here keeps that answer rather than inventing a
				// pause.
				let review: string | undefined;
				try {
					const agents = await api.get<AgentRow[]>(
						`/api/user/${userId}/agents/`,
					);
					review = reviewStatusOf(agents ?? [], strategyId);
				} catch (err) {
					logger.error('activate-strategy: could not read the review', err);
				}
				if (!cancelled) setDone(review === 'paused' ? 'paused' : 'activated');
			} catch (err) {
				logger.error('activate-strategy: could not load the strategy', err);
				// Only a 404 proves the strategy is gone. A 500, a dropped request
				// or an expired token all used to say the same thing, and the
				// texted link is permanent, so the owner met it again every time.
				if (!cancelled)
					setLoadError(
						err instanceof RestApiError && err.status === 404
							? 'missing'
							: 'unavailable',
					);
			}
		};
		if (userId && strategyId) void load();
		return () => {
			cancelled = true;
		};
	}, [api, userId, strategyId, reloadKey]);

	const activate = useCallback(async () => {
		setBusy('activate');
		setError(null);
		try {
			await api.post(`/api/user/${userId}/agents/activate-strategy`, {
				strategy_id: strategyId,
			});
			setDone('activated');
		} catch (err) {
			logger.error('activate-strategy: activation failed', err);
			setError(
				'That did not go through. Nothing has started. Please try again.',
			);
		} finally {
			setBusy(null);
		}
	}, [api, userId, strategyId]);

	// Resume, never activate. Activation builds the campaigns again and raises a
	// setup notification per campaign, and this strategy is already built.
	const resume = useCallback(async () => {
		setBusy('resume');
		setError(null);
		try {
			await api.post(`/api/user/${userId}/agents/resume-strategy`, {
				strategy_id: strategyId,
			});
			setDone('activated');
		} catch (err) {
			logger.error('activate-strategy: resume failed', err);
			setError(
				'That did not go through. It is still paused. Please try again.',
			);
		} finally {
			setBusy(null);
		}
	}, [api, userId, strategyId]);

	const reject = useCallback(async () => {
		setBusy('reject');
		setError(null);
		try {
			await api.delete(`/api/user/${userId}/strategies/${strategyId}`);
			setDone('rejected');
		} catch (err) {
			logger.error('activate-strategy: reject failed', err);
			setError('That did not go through. The strategy is still here.');
		} finally {
			setBusy(null);
		}
	}, [api, userId, strategyId]);

	if (loadError === 'missing') {
		return (
			<main className={styles.page}>
				<h1 className={styles.title}>This strategy is not here</h1>
				<p className={styles.note}>
					Reply to the text and Bridgeway will send you a fresh link.
				</p>
			</main>
		);
	}

	if (loadError === 'unavailable') {
		return (
			<main className={styles.page}>
				<h1 className={styles.title}>Bridgeway could not load this</h1>
				<p className={styles.note}>
					Your strategy is still here. Something went wrong on the way to it.
				</p>
				<button
					type="button"
					className={styles.activate}
					onClick={() => {
						setLoadError(null);
						setReloadKey((k) => k + 1);
					}}
					data-testid="retry-load"
				>
					Try again
				</button>
			</main>
		);
	}

	if (!strategy) {
		return (
			<main className={styles.page}>
				<p className={styles.note}>Loading…</p>
			</main>
		);
	}

	if (done === 'paused') {
		return (
			<main className={styles.page}>
				<div
					className={styles.done}
					role="status"
					data-testid="strategy-paused"
				>
					<p className={styles.doneTitle}>Strategy is paused</p>
					<p className={styles.note}>
						The daily review is off, and the campaigns it started have stopped
						sending. Nothing goes out until you start it again.
					</p>
				</div>
				<button
					type="button"
					className={styles.activate}
					onClick={resume}
					disabled={busy !== null}
					data-testid="resume-strategy"
				>
					{busy === 'resume' ? 'Starting it again…' : 'Start it again'}
				</button>
				{error && (
					<p className={styles.error} role="alert">
						{error}
					</p>
				)}
			</main>
		);
	}

	if (done) {
		return (
			<main className={styles.page}>
				<div
					className={styles.done}
					role="status"
					data-testid={`strategy-${done}`}
				>
					<p className={styles.doneTitle}>
						{done === 'activated' ? 'Strategy is on' : 'Strategy discarded'}
					</p>
					<p className={styles.note}>
						{done === 'activated'
							? 'Bridgeway reviews it daily and will text you what it finds. Each campaign still needs your go-ahead before anything sends.'
							: 'Nothing from it will run. You can close this page.'}
					</p>
				</div>
			</main>
		);
	}

	const campaigns = strategy.campaign_proposals ?? [];
	const aim =
		strategy.direction?.wish?.outcome ??
		strategy.goal?.outcome ??
		strategy.direction?.statement;

	return (
		<main className={styles.page}>
			<h1 className={styles.title}>Turn this strategy on?</h1>
			{aim && <p className={styles.aim}>{aim}</p>}

			{campaigns.length > 0 && (
				<>
					<p className={styles.facts}>
						{campaigns.length === 1
							? '1 campaign in the plan'
							: `${campaigns.length} campaigns in the plan`}
					</p>
					<ul className={styles.campaigns}>
						{campaigns.map((c) => (
							<li key={c.id}>
								<span className={styles.campaignTitle}>{c.title || c.id}</span>
								{c.aim && <span className={styles.campaignAim}>{c.aim}</span>}
							</li>
						))}
					</ul>
				</>
			)}

			<button
				type="button"
				className={styles.activate}
				onClick={activate}
				disabled={busy !== null}
				data-testid="activate-strategy"
			>
				{busy === 'activate' ? 'Turning it on…' : 'Turn on this strategy'}
			</button>
			<p className={styles.note}>
				This starts the daily review and builds the campaigns. Nothing is sent
				to your customers until you start a campaign.
			</p>

			<button
				type="button"
				className={styles.reject}
				onClick={reject}
				disabled={busy !== null}
				data-testid="reject-strategy"
			>
				{busy === 'reject' ? 'Discarding…' : 'No thanks, discard it'}
			</button>

			{error && (
				<p className={styles.error} role="alert">
					{error}
				</p>
			)}
		</main>
	);
};
